/**
 * PostHog error tracking utilities
 * Client-side only - safe to call from anywhere
 *
 * Usage examples:
 *
 * // In a try/catch block:
 * try {
 *   await someAsyncOperation()
 * } catch (error) {
 *   capturePostHogError(error, { operation: 'someAsyncOperation' })
 * }
 *
 * // For API errors:
 * if (!response.ok) {
 *   capturePostHogError(new Error(`API Error: ${response.status}`), {
 *     status: response.status,
 *     endpoint: '/api/some-endpoint'
 *   })
 * }
 */

export function capturePostHogError(
	error: unknown,
	context?: {
		status?: number
		pathname?: string
		route_id?: string
		user_id?: string
		[key: string]: any
	},
) {
	if (typeof window === 'undefined') return

	const ph = (window as any).posthog
	if (!ph) return

	const errorMessage = error instanceof Error ? error.message : String(error)
	const errorStack = error instanceof Error ? error.stack : undefined

	ph.capture('route:error', {
		error_message: errorMessage,
		error_stack: errorStack,
		error_type: error?.constructor?.name || 'Unknown',
		timestamp: new Date().toISOString(),
		url: window.location.href,
		user_agent: navigator.userAgent,
		...context,
	})
}

export function capturePostHogRouteError(
	status: number,
	pathname: string,
	errorData?: any,
) {
	if (typeof window === 'undefined') return

	const ph = (window as any).posthog
	if (!ph) return

	ph.capture('route:error', {
		status,
		pathname,
		error_type: 'RouteError',
		error_message: errorData?.message || `HTTP ${status} Error`,
		error_data: errorData,
		timestamp: new Date().toISOString(),
		url: window.location.href,
		referrer: document.referrer || undefined,
	})
}

export function capturePostHogException(
	error: unknown,
	tags?: Record<string, string>,
) {
	if (typeof window === 'undefined') return

	const ph = (window as any).posthog
	if (!ph) return

	ph.capture('$exception', {
		$exception_message: error instanceof Error ? error.message : String(error),
		$exception_type: error?.constructor?.name || 'Unknown',
		$exception_stack_trace_raw:
			error instanceof Error ? error.stack : undefined,
		timestamp: new Date().toISOString(),
		...tags,
	})
}
